import {
  formatParentNameWithId,
  resolveParentCandidatesForNodeType,
} from "pages/DataManagement/NodeList/nodeListRowsMapper";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";

function toNodeId(value) {
  if (isNullUndefinedOrEmptyString(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatParentNodeValue(node) {
  if (!node) return "";
  const syntheticRoot =
    node.nodeId === -1 || String(node.id) === "context-root";
  return formatParentNameWithId(node.name, node.nodeId, {
    hideId: syntheticRoot,
  });
}

export function filterBranchTree(node, query) {
  if (!node) return null;
  const normalizedQuery = (query || "").trim().toLowerCase();
  if (!normalizedQuery) return node;

  const filterChildren = (children) => {
    if (!children?.length) return [];
    return children
      .map((childNode) => filterBranchTree(childNode, query))
      .filter(Boolean);
  };

  const nameLower = String(node.name ?? "").toLowerCase();
  const idStr = node.nodeId != null ? String(node.nodeId) : "";
  const isNameMatch =
    nameLower.includes(normalizedQuery) ||
    idStr.toLowerCase().includes(normalizedQuery);
  const filteredChildren = filterChildren(node.children);

  if (isNameMatch) {
    return { ...node, children: node.children || [] };
  }
  if (filteredChildren.length > 0) {
    return { ...node, children: filteredChildren };
  }
  return null;
}

export function buildParentSelectionTree(contextNodes) {
  if (!Array.isArray(contextNodes) || contextNodes.length === 0) {
    return null;
  }

  const nodeMap = new Map();
  contextNodes.forEach((contextNode) => {
    if (String(contextNode?.nodeType || "").toLowerCase() === "tag") {
      return;
    }
    const nodeId = toNodeId(contextNode?.nodeId);
    if (nodeId === null) return;
    const nodeName = String(contextNode?.nodeName || nodeId);
    nodeMap.set(nodeId, {
      id: String(nodeId),
      nodeId,
      name: nodeName,
      children: [],
    });
  });

  const roots = [];
  contextNodes.forEach((contextNode) => {
    if (String(contextNode?.nodeType || "").toLowerCase() === "tag") return;
    const nodeId = toNodeId(contextNode?.nodeId);
    if (nodeId === null) return;

    const childNode = nodeMap.get(nodeId);
    if (!childNode) return;

    const parentId = toNodeId(contextNode?.parentNodeId);
    const parentNode = isFiniteNumber(parentId) ? nodeMap.get(parentId) : null;
    if (parentNode) {
      parentNode.children.push(childNode);
    } else {
      roots.push(childNode);
    }
  });

  if (roots.length === 1) {
    return roots[0];
  }

  return {
    id: "context-root",
    nodeId: -1,
    name: "All Context Nodes",
    children: roots,
  };
}

export function buildAllowedParentTree(
  referencePayload,
  rowNodeType,
  parentCandidates,
  customerName
) {
  const filtered = resolveParentCandidatesForNodeType(
    referencePayload,
    rowNodeType,
    parentCandidates,
    customerName
  );
  if (!filtered.length) {
    return null;
  }
  const asContext = filtered.map((c) => ({
    nodeId: c?.nodeId,
    nodeName: c?.nodeName,
    nodeType: c?.nodeType,
    parentNodeId: isFiniteNumber(c?.parentNodeId)
      ? Number(c?.parentNodeId)
      : null,
  }));
  return buildParentSelectionTree(asContext);
}
